/* Option chain with greeks.
 *
 * Calls on the left, puts on the right, strikes down the middle — the layout
 * every Indian options trader reads without thinking, which is the point.
 *
 * Two things this panel refuses to do:
 *
 *   * Invent strikes. If the instrument master holds no option contracts for
 *     the underlying, it says so and shows nothing. A generated chain is
 *     indistinguishable from a real one on screen, and someone would trade it.
 *
 *   * Present greeks as observations. They are Black-Scholes values computed
 *     from an assumed volatility, and the assumption is an input the user can
 *     see and change. A delta shown without its volatility input is a fact
 *     about someone's guess wearing the clothes of a market fact.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { useStore } from '@/store'
import { fmtPrice } from '@/lib/format'
import { Empty } from '@/components/viz'
import type { OptionChain as Chain, OptionLeg } from '@/types'

export function OptionChain() {
  const instrument = useStore((s) => s.selectedInstrument)
  const [chain, setChain] = useState<Chain | null>(null)
  const [expiry, setExpiry] = useState<string | undefined>()
  const [vol, setVol] = useState(0.20)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!instrument) { setChain(null); return }
    let cancelled = false
    setLoading(true)
    api.optionChain(String(instrument), { expiry, vol })
      .then((c) => { if (!cancelled) { setChain(c); setError(null) } })
      .catch((e) => { if (!cancelled) { setChain(null); setError(String(e)) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [instrument, expiry, vol])

  if (!instrument) {
    return <Panel title="Option chain"><Empty>Select an underlying</Empty></Panel>
  }
  if (loading && !chain) {
    return <Panel title="Option chain"><Empty>Loading…</Empty></Panel>
  }
  if (error || !chain) {
    return (
      <Panel title={`Option chain · ${instrument}`}>
        <Empty>{instrument} is not in the instrument master, or its chain could not be loaded.</Empty>
      </Panel>
    )
  }

  if (chain.strikes.length === 0) {
    return (
      <Panel title={`Option chain · ${chain.underlying}`}>
        <div style={{
          margin: 'var(--sp-3)', padding: 'var(--sp-3)',
          background: 'var(--bg-input)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)', lineHeight: 1.6,
        }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 6 }}>
            No option contracts loaded for {chain.underlying}
          </div>
          {chain.note ?? (
            'The instrument master holds no active option contracts for this ' +
            'underlying, so there is no chain to display.'
          )}
          <div style={{ marginTop: 8, color: 'var(--text-muted)' }}>
            This is shown as empty rather than filled with generated strikes: a
            manufactured chain looks identical to a real one on screen, and
            someone would trade it.
          </div>
        </div>
      </Panel>
    )
  }

  const spot = Number(chain.spot ?? 0)

  return (
    <Panel
      title={`Option chain · ${chain.underlying}`}
      noPadding
      actions={
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <select
            value={chain.expiry ?? ''}
            onChange={(e) => setExpiry(e.target.value)}
            aria-label="Expiry"
            style={ctl}
          >
            {chain.expiries.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
          }}>
            IV in
            <input
              type="number" min={0.01} max={3} step={0.01} value={vol}
              onChange={(e) => setVol(Number(e.target.value) || 0.2)}
              className="num"
              style={{ ...ctl, width: 58 }}
            />
          </label>
        </div>
      }
    >
      {/* The greeks caveat, stated once and prominently. */}
      <div style={{
        padding: 'var(--sp-2) var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap',
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
      }}>
        <span>
          Spot <b className="num" style={{ color: 'var(--text-primary)' }}>
            {fmtPrice(chain.spot)}
          </b>
          {chain.spot_source && ` (${chain.spot_source})`}
        </span>
        {chain.atm_strike && (
          <span>ATM <b className="num" style={{ color: 'var(--accent)' }}>{chain.atm_strike}</b></span>
        )}
        {chain.days_to_expiry !== undefined && <span>{chain.days_to_expiry} days to expiry</span>}
        <span style={{ color: 'var(--warn)' }}>
          Greeks are COMPUTED (Black-Scholes) at IV {vol.toFixed(2)}, not implied
          from traded prices — change IV and every greek changes.
        </span>
      </div>

      <div className="scroll-x">
        <table className="qx">
          <thead>
            <tr>
              <th colSpan={5} style={{ textAlign: 'center', color: 'var(--buy)' }}>Calls</th>
              <th className="r" style={{ textAlign: 'center' }}>Strike</th>
              <th colSpan={5} style={{ textAlign: 'center', color: 'var(--sell)' }}>Puts</th>
            </tr>
            <tr>
              <th className="r">Delta</th><th className="r">Theta</th>
              <th className="r">Bid</th><th className="r">Ask</th><th className="r">LTP</th>
              <th className="r" style={{ textAlign: 'center' }}>—</th>
              <th className="r">LTP</th><th className="r">Bid</th><th className="r">Ask</th>
              <th className="r">Theta</th><th className="r">Delta</th>
            </tr>
          </thead>
          <tbody>
            {chain.strikes.map((s) => {
              const k = Number(s.strike)
              const atm = chain.atm_strike === s.strike
              // In-the-money shading, the convention every chain uses: calls
              // below spot and puts above it.
              const callItm = spot > 0 && k < spot
              const putItm = spot > 0 && k > spot
              return (
                <tr key={s.strike} style={{
                  background: atm ? 'var(--accent-bg)' : undefined,
                }}>
                  <Leg leg={s.call} field="delta" itm={callItm} />
                  <Leg leg={s.call} field="theta" itm={callItm} />
                  <Leg leg={s.call} field="bid" itm={callItm} />
                  <Leg leg={s.call} field="ask" itm={callItm} />
                  <Leg leg={s.call} field="last" itm={callItm} strong />
                  <td className="num r" style={{
                    textAlign: 'center', fontWeight: 700,
                    color: atm ? 'var(--accent)' : 'var(--text-primary)',
                    borderLeft: '1px solid var(--border)',
                    borderRight: '1px solid var(--border)',
                  }}>
                    {s.strike}
                  </td>
                  <Leg leg={s.put} field="last" itm={putItm} strong />
                  <Leg leg={s.put} field="bid" itm={putItm} />
                  <Leg leg={s.put} field="ask" itm={putItm} />
                  <Leg leg={s.put} field="theta" itm={putItm} />
                  <Leg leg={s.put} field="delta" itm={putItm} />
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Leg({
  leg, field, itm, strong,
}: {
  leg: OptionLeg | null
  field: 'delta' | 'theta' | 'bid' | 'ask' | 'last'
  itm: boolean
  strong?: boolean
}) {
  const bg = itm ? 'rgba(245, 158, 11, 0.07)' : undefined

  if (!leg) {
    return <td className="num r" style={{ background: bg, color: 'var(--text-muted)' }}>—</td>
  }

  let text = '—'
  let color: string | undefined
  if (field === 'delta' || field === 'theta') {
    const v = leg.greeks[field]
    // A greek that could not be computed shows a dash, never a zero. Zero delta
    // is a meaningful value and must not stand in for "unknown".
    text = v == null ? '—' : v.toFixed(field === 'delta' ? 3 : 2)
    color = 'var(--text-muted)'
  } else {
    text = fmtPrice(leg[field])
    if (strong) color = 'var(--text-primary)'
  }

  return (
    <td className="num r" style={{
      background: bg, color, fontWeight: strong ? 600 : undefined,
    }}>
      {text}
    </td>
  )
}

const ctl: React.CSSProperties = {
  padding: '1px var(--sp-2)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
  fontSize: 'var(--fs-2xs)', fontFamily: 'inherit',
}
